import { useState, useMemo } from 'react';
import type { 
  GlossarioTerm, 
  GlossarioCategory,
  GlossarioData,
  UseGlossarioReturn 
} from '../types/glossario';
import glossarioDataRaw from '../data/glossario.json';

const glossarioData = glossarioDataRaw as GlossarioData;

/**
 * Hook para gerenciar estado e lógica do Glossário Academy
 * 
 * Features:
 * - Busca por termo (case-insensitive, busca em termo + definições)
 * - Filtro por categoria
 * - Seleção de termo para exibição detalhada
 * - 45 termos profissionais de valuation
 * 
 * @example
 * ```tsx
 * const { termosFiltrados, setTermoBusca, setCategoriaAtiva } = useGlossario();
 * ```
 */
export function useGlossario(): UseGlossarioReturn {
  const [categoriaAtiva, setCategoriaAtiva] = useState<string | 'todos'>('todos');
  const [termoBusca, setTermoBusca] = useState('');
  const [termoSelecionado, selecionarTermo] = useState<GlossarioTerm | null>(null);

  // Carregar termos e categorias do JSON
  const termos = useMemo<GlossarioTerm[]>(() => {
    return glossarioData.termos;
  }, []);

  const categorias = useMemo<GlossarioCategory[]>(() => {
    return glossarioData.categorias;
  }, []);

  // Filtrar termos por categoria e busca
  const termosFiltrados = useMemo(() => {
    let resultado = termos;

    // Filtro por categoria
    if (categoriaAtiva !== 'todos') {
      resultado = resultado.filter(termo => termo.categoria === categoriaAtiva);
    }

    // Filtro por busca (termo, definição_curta, definição_completa ou tags)
    if (termoBusca.trim()) {
      const buscaLower = termoBusca.toLowerCase();
      resultado = resultado.filter(termo => 
        termo.termo.toLowerCase().includes(buscaLower) ||
        termo.definicao_curta.toLowerCase().includes(buscaLower) ||
        termo.definicao_completa.toLowerCase().includes(buscaLower) ||
        termo.tags?.some(tag => tag.toLowerCase().includes(buscaLower))
      );
    }

    return resultado;
  }, [termos, categoriaAtiva, termoBusca]);

  // Buscar termo específico por nome exato, ID ou correspondência parcial
  const buscarTermo = (nome: string): GlossarioTerm | undefined => {
    const nomeOriginal = nome.trim();
    const nomeLower = nomeOriginal.toLowerCase();
    
    // 1. Busca exata por ID ou nome do termo
    let termo = termos.find(
      t => t.termo.toLowerCase() === nomeLower || 
      t.id === nomeLower
    );
    
    if (termo) return termo;
    
    // 2. Tentar match parcial por palavras-chave (ex: "WACC" em "Cálculo do WACC")
    // Extrair palavras-chave importantes (ignorando preposições e palavras comuns)
    const palavrasIgnoradas = ['de', 'do', 'da', 'dos', 'das', 'e', 'o', 'a', 'os', 'as', 'no', 'na', 'nos', 'nas', 'em', 'para', 'com', 'por', 'base', 'cálculo', 'cálculos', 'página', 'somando', 'aba', 'mostra', 'geralmente', 'anos', 'projetados', 'descontados', 'sistema', 'taxa', 'caixa', 'fluxo', 'livre', 'desconto'];
    
    // Processar palavras originais E lowercase para detectar siglas
    const palavrasOriginais = nomeOriginal.split(/[\s()]+/).filter(p => p.length > 2);
    const palavras = nomeLower.split(/[\s()]+/).filter(p => p.length > 2 && !palavrasIgnoradas.includes(p));
    
    // Buscar termo cujo nome contenha alguma palavra-chave significativa
    // Priorizar palavras técnicas (siglas que estavam em maiúsculas no original)
    const palavrasTecnicas = palavrasOriginais
      .filter((p) => {
        const lower = p.toLowerCase();
        // Sigla técnica se: estava em maiúsculas E tem 2-5 chars E não é palavra ignorada
        return p === p.toUpperCase() && p.length >= 2 && p.length <= 5 && !palavrasIgnoradas.includes(lower);
      })
      .map(p => p.toUpperCase());
    
    if (palavrasTecnicas.length > 0) {
      // Tentar match exato com siglas técnicas
      termo = termos.find(t => {
        const termoUpper = t.termo.toUpperCase();
        return palavrasTecnicas.some(sigla => 
          termoUpper.includes(sigla.toUpperCase()) || 
          t.id.toUpperCase().includes(sigla.toUpperCase())
        );
      });
      
      if (termo) return termo;
    }
    
    // Fallback: buscar por palavras-chave gerais (threshold 30%)
    termo = termos.find(t => {
      const termoLower = t.termo.toLowerCase();
      const matches = palavras.filter(palavra => termoLower.includes(palavra));
      return matches.length > 0 && matches.length >= Math.ceil(palavras.length * 0.3);
    });
    
    if (termo) return termo;
    
    // 3. Buscar por tags (caso as referências sejam similares às tags)
    termo = termos.find(t => 
      t.tags?.some(tag => tag.toLowerCase().includes(nomeLower) || nomeLower.includes(tag.toLowerCase()))
    );
    
    return termo;
  };

  // Obter nome da categoria por ID
  const getCategoriaName = (categoryId: string): string => {
    const categoria = categorias.find(cat => cat.id === categoryId);
    return categoria?.nome || '';
  };

  return {
    termos,
    categorias,
    termosFiltrados,
    categoriaAtiva,
    setCategoriaAtiva,
    termoBusca,
    setTermoBusca,
    termoSelecionado,
    selecionarTermo,
    buscarTermo,
    getCategoriaName,
  };
}
